import { useEffect, useMemo, useState } from 'react'
import { doc, collection, onSnapshot, setDoc } from 'firebase/firestore'
import { db } from '@/shared/firebase/client'
import { useAuth } from '@/features/auth/AuthProvider'
import { subscribeToNewsletters, type Newsletter } from './repository/newsletterRepository'
import { FlowDetail, missingTags, type FlowTab } from './automation/FlowDetail'
import { PERIOD_LABEL, useAutomationStats } from './automation/useAutomationStats'
import { FlowStatsModal } from './automation/FlowStatsModal'
import { canStart } from './automation/flowText'
import {
  FLOWS,
  FOLDERS,
  isWired,
  type Flow,
  type Folder,
} from '../../../functions-webhook/src/automation/flows'
import './Automation.css'

/**
 * Email automation — the list of lifecycle flows T01–T21, 13 Oct 2026.
 *
 * Artboard 1 of the design canvas ("Email automation canvas – proposal"). The
 * flows are code (`functions-webhook/src/automation/flows.ts`), imported here by
 * path so the list and the engine read one definition; this screen stores only
 * the switch, in `skidaCrmAutomations/{id}`. See docs/EMAIL_AUTOMATION.md.
 *
 * The eleven flows this screen used to show were retired the same day: none
 * was ever wired to an event, six overlapped T01, T05, T06, T10, T14 and T19,
 * and four were Skida PRO flows.
 *
 * ## Two facts per row, kept apart
 *
 * Whether we intend a flow to run (the switch) and whether anything can fire it
 * ("Waits for app event"). A switched-on flow whose event does not exist yet
 * sends nothing, and the row says so rather than looking live.
 *
 * ## The email a flow sends
 *
 * A draft from the Newsletter composer (Skida App sender), chosen per row and
 * stored as `templateId`. A flow cannot be switched on without one: the
 * scheduler would only end every run with "No template chosen".
 */

const PLAN_URL = 'https://claude.ai/artifact/75Xri3iAp6Q9FnnrH1QcT8'

/** See `canStart`: a flow nothing can start yet says so on its row. */
const startsNow = canStart

type FolderFilter = 'All' | Folder

/**
 * The open automation lives in the address — `?flow=T05&tab=settings` — so a
 * link to one works and the back button leaves it.
 */
function readOpen(): { flow: string; tab: FlowTab } | null {
  const q = new URLSearchParams(window.location.search)
  const flow = q.get('flow')
  if (!flow || !FLOWS.some((f) => f.id === flow)) return null
  const tab = q.get('tab')
  return { flow, tab: tab === 'settings' || tab === 'test' ? tab : 'canvas' }
}

const STREAM_LABEL = { automation: 'Account', newsletter: 'Newsletter' } as const
const TYPE_LABEL = { transactional: 'Transactional', lifecycle: 'Lifecycle', marketing: 'Marketing' } as const

export function Automation({ product }: { product: 'app' | 'pro' }) {
  const { isAdmin } = useAuth()
  const [enabled, setEnabled] = useState<Record<string, boolean>>({})
  const [templates, setTemplates] = useState<Record<string, string>>({})
  const [drafts, setDrafts] = useState<Newsletter[]>([])
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [folder, setFolder] = useState<FolderFilter>('All')
  const [q, setQ] = useState('')
  const [open, setOpen] = useState(readOpen)
  const stats = useAutomationStats((e) => setError(e.message))
  /** The flow whose figures are open — 28 Oct 2026. */
  const [statsFor, setStatsFor] = useState<Flow | null>(null)

  useEffect(() => {
    const onPop = () => setOpen(readOpen())
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  const go = (next: { flow: string; tab: FlowTab } | null) => {
    const search = next
      ? `?flow=${next.flow}${next.tab === 'canvas' ? '' : `&tab=${next.tab}`}`
      : ''
    window.history.pushState(null, '', `${window.location.pathname}${search}`)
    setOpen(next)
    setError(null)
  }

  useEffect(
    () =>
      onSnapshot(
        collection(db, 'skidaCrmAutomations'),
        (snap) => {
          const next: Record<string, boolean> = {}
          const tpl: Record<string, string> = {}
          for (const d of snap.docs) {
            next[d.id] = d.data().enabled === true
            if (typeof d.data().templateId === 'string') tpl[d.id] = d.data().templateId
          }
          setEnabled(next)
          setTemplates(tpl)
        },
        (e) => setError(e.message),
      ),
    [],
  )

  // The composer's drafts for the app's sender — the emails a flow can send.
  useEffect(
    () =>
      subscribeToNewsletters(
        'consumer',
        (rows) => setDrafts(rows.filter((n) => n.status === 'draft')),
        (e) => setError(e.message),
      ),
    [],
  )

  const rows = useMemo(() => {
    const term = q.trim().toLowerCase()
    return FLOWS.filter((f) => folder === 'All' || f.folder === folder).filter(
      (f) => !term || `${f.id} ${f.name} ${f.summary}`.toLowerCase().includes(term),
    )
  }, [folder, q])

  // Off unless somebody switched it on — every flow starts off.
  const isOn = (f: Flow) => enabled[f.id] === true

  const save = async (f: Flow, patch: { enabled?: boolean; templateId?: string }) => {
    setBusy(f.id)
    setError(null)
    try {
      await setDoc(
        doc(db, 'skidaCrmAutomations', f.id),
        { ...patch, name: `${f.id} ${f.name}`, product: 'app' },
        { merge: true },
      )
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(null)
    }
  }

  // Every Send email block of the flow, summed.
  const sumOf = (f: Flow, metric: 'sent' | 'opened' | 'clicked'): string => {
    const c = stats[f.id]
    if (!c) return '–'
    const n = f.blocks
      .filter((b) => b.kind === 'send')
      .reduce((acc, b) => acc + (c[`${b.id}__${metric}`] ?? 0), 0)
    return n.toLocaleString('en-GB')
  }

  // A draft that was deleted, or sent as a newsletter, still has its id here.
  const templateOf = (f: Flow) => drafts.find((d) => d.id === templates[f.id])
  const hasTemplate = (f: Flow) => Boolean(templates[f.id])

  const toggle = (f: Flow) => {
    if (!isOn(f) && !hasTemplate(f)) {
      setError(`Choose the email ${f.id} sends before switching it on.`)
      return
    }
    const missing = missingTags(templateOf(f))
    if (!isOn(f) && missing.length > 0) {
      setError(`The email for ${f.id} uses merge tags the CRM cannot fill: ${missing.join(', ')}.`)
      return
    }
    void save(f, { enabled: !isOn(f) })
  }

  const choose = (f: Flow, templateId: string) => {
    // Clearing the email of a running flow would end every run it has.
    if (!templateId && isOn(f)) {
      setError(`Switch ${f.id} off before removing its email.`)
      return
    }
    void save(f, { templateId })
  }

  if (product === 'pro') {
    return (
      <main className="autom">
        <h2 className="autom__title">Email automation</h2>
        <p className="autom__sub">
          No Skida PRO automations. The four PRO flows listed here before were never wired to an
          event and were retired on 13 Oct 2026. T21 (Guide signal) is on the Skida App side: it
          speaks to app users who look like guides.
        </p>
      </main>
    )
  }

  const openFlow = open && FLOWS.find((f) => f.id === open.flow)
  if (openFlow && open) {
    return (
      <FlowDetail
        flow={openFlow}
        tab={open.tab}
        onTab={(tab) => go({ flow: openFlow.id, tab })}
        onBack={() => go(null)}
        on={isOn(openFlow)}
        canEdit={isAdmin}
        busy={busy === openFlow.id}
        templateId={templates[openFlow.id] ?? ''}
        drafts={drafts}
        counts={stats[openFlow.id]}
        error={error}
        onToggle={() => toggle(openFlow)}
        onChoose={(id) => choose(openFlow, id)}
      />
    )
  }

  const onCount = FLOWS.filter(isOn).length
  const counts = (f: FolderFilter) =>
    f === 'All' ? FLOWS.length : FLOWS.filter((x) => x.folder === f).length

  return (
    <main className="autom">
      <div className="autom__head">
        <div>
          <h2 className="autom__title">Email automation</h2>
          <p className="autom__sub">
            Every automated email in one place: which event sends which email.
          </p>
        </div>
        <a className="btn btn--ghost" href={PLAN_URL} target="_blank" rel="noreferrer">
          Lifecycle plan
        </a>
      </div>

      <div className="autom__stats">
        <div className="autom__stat">
          <span>Automations</span>
          <strong>{FLOWS.length}</strong>
        </div>
        <div className="autom__stat">
          <span>Switched on</span>
          <strong>{onCount}</strong>
        </div>
        <div className="autom__stat">
          <span>Phase 1, before mid-November</span>
          <strong>{FLOWS.filter((f) => f.phase === 1).length}</strong>
        </div>
        <div className="autom__stat">
          <span>Waiting for an app event</span>
          <strong>{FLOWS.filter((f) => !isWired(f)).length}</strong>
        </div>
      </div>

      {/* Stated once, loudly, rather than implied by a column nobody reads. */}
      <div className="autom__banner">
        <strong>How a flow starts sending.</strong> Write the email as a draft in the Newsletter
        composer (Skida App sender), choose it on the flow's row, then switch the flow on. Only
        people who start a trial or subscription after that are sent anything — switching on never
        sends to a backlog. Keep the draft as a draft: it is the email the flow sends.
      </div>

      {error && <div className="empty-note">Could not save: {error}</div>}

      <div className="autom__tools">
        <div className="autom__folders" role="group" aria-label="Folders">
          {(['All', ...FOLDERS] as FolderFilter[]).map((f) => (
            <button
              key={f}
              type="button"
              className="chip"
              aria-pressed={folder === f}
              onClick={() => setFolder(f)}
            >
              {f} <span className="autom__count">{counts(f)}</span>
            </button>
          ))}
        </div>
        <label className="autom__search">
          <span className="autom__sr">Search automations</span>
          <input
            className="input"
            type="search"
            placeholder="Search by name, ID or trigger"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </label>
      </div>

      <div className="autom__table" role="table" aria-label="Automations">
        <div className="autom__grid autom__colhead" role="row">
          <span role="columnheader">ID</span>
          <span role="columnheader">Automation</span>
          <span role="columnheader">Trigger</span>
          <span role="columnheader">Stream</span>
          <span role="columnheader">Email</span>
          <span role="columnheader">On</span>
          <span role="columnheader" className="autom__num">Sent</span>
          <span role="columnheader" className="autom__num">Opens</span>
          <span role="columnheader" className="autom__num">Clicks</span>
        </div>

        {rows.map((f) => {
          const on = isOn(f)
          const wired = isWired(f)
          return (
            <div className="autom__grid autom__row" role="row" key={f.id}>
              <span role="cell" className="autom__id">{f.id}</span>
              <span role="cell">
                <button type="button" className="autom__name autom__open" onClick={() => go({ flow: f.id, tab: 'canvas' })}>
                  {f.name}
                </button>
                <span className="autom__meta">
                  {f.folder} · {TYPE_LABEL[f.type]} · Phase {f.phase}
                </span>
              </span>
              <span role="cell" className="autom__trigger">
                {f.summary}
                {!wired && <span className="autom__gated">Waits for app event</span>}
                {wired && !startsNow(f) && <span className="autom__gated">Not running yet</span>}
              </span>
              <span role="cell">
                <span className={`pill autom__stream autom__stream--${f.stream}`}>
                  {STREAM_LABEL[f.stream]}
                </span>
              </span>
              <span role="cell">
                <select
                  className="input autom__tpl"
                  aria-label={`Email ${f.id} sends`}
                  value={templates[f.id] ?? ''}
                  disabled={!isAdmin || busy === f.id}
                  onChange={(e) => choose(f, e.target.value)}
                >
                  <option value="">Choose a draft…</option>
                  {/* Kept visible if it is no longer a draft, so the choice is never hidden. */}
                  {hasTemplate(f) && !templateOf(f) && (
                    <option value={templates[f.id]}>Not a draft any more</option>
                  )}
                  {drafts.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name || d.subject || '(no subject)'}
                    </option>
                  ))}
                </select>
              </span>
              <span role="cell">
                <button
                  type="button"
                  role="switch"
                  aria-checked={on}
                  aria-label={`${on ? 'Switch off' : 'Switch on'} ${f.id} ${f.name}`}
                  className={`autom__switch${on ? ' autom__switch--on' : ''}`}
                  disabled={!isAdmin || busy === f.id}
                  title={isAdmin ? undefined : 'An admin sets these.'}
                  onClick={() => toggle(f)}
                >
                  <span />
                </button>
              </span>
              {/* A dash, not a nought, for a flow with no counts in the period:
                  not having run is different from having run and sent none. */}
              {(['sent', 'opened', 'clicked'] as const).map((m) => (
                <span role="cell" className="autom__num" key={m}>
                  {/* Opens the flow's figures: deliveries, bounces, spam,
                      unsubscribes and the links pressed — 28 Oct 2026. */}
                  <button
                    type="button"
                    className="autom__statbtn"
                    title="Delivered, bounced, unsubscribed and the links pressed"
                    onClick={() => setStatsFor(f)}
                  >
                    {sumOf(f, m)}
                  </button>
                </span>
              ))}
            </div>
          )
        })}
        {rows.length === 0 && <p className="autom__empty">No automations match your search.</p>}
        <p className="autom__foot">
          {PERIOD_LABEL}. A flow that has not run in that time shows –. Click a figure for its
          deliveries, unsubscribes and the links pressed; open a flow to see its canvas and the
          counts on every block.
        </p>
      </div>

      {!isAdmin && (
        <p className="autom__note">
          An admin switches these, under the same rule as pipeline stages and templates.
        </p>
      )}
      {statsFor && (
        <FlowStatsModal flow={statsFor} counts={stats[statsFor.id]} onClose={() => setStatsFor(null)} />
      )}
    </main>
  )
}
